import {
  Vector3,
  BufferGeometry,
  Float32BufferAttribute,
  ShaderMaterial,
  Points,
  AdditiveBlending,
  MathUtils,
} from 'three';

import { attrTailDust, attrTailGas } from '../configEntities/groupsAttr.js';
import vertexComet from '../../../shaders/outflow/vertexTail.glsl';
import fragmentComet from '../../../shaders/outflow/fragmentTail.glsl';
import { colorSizeAleat } from './galaxy.js';
import { sceneManager } from '../script.js';

export class Tail {
  constructor(mesh, center, pointCount) {
    this.mesh = mesh;
    this.center = center;
    this.scene = sceneManager.scene;
    this.pointCountDust = pointCount;
    this.pointCountGas = Math.floor(pointCount * 0.7);

    this.CONFIG = {
      maxRadius: 20,
      maxCameraDist: 5,
      minFalloffExp: 0.4,
      maxFalloffExp: 0.8,
      globalOpacity: 0.8,
      dustSphereRadius: 0.016,
      gasSphereRadius: 0.012,
      gasSpeedFactor: 0.01,
      dustSpeedFactor: 0.012,
      resetProbability: { default: 0.01, randomMove: { gas: 0.01, dust: 0.01 }}
    };
    this.attrsDust = this._generateAttributes(this.pointCountDust, attrTailDust);
    this.attrsGas = this._generateAttributes(this.pointCountGas, attrTailGas);

    this.dust = this._createPoints(this.attrsDust);
    this.gas = this._createPoints(this.attrsGas);

    this._removed = false;
  }
  _generateAttributes(count, attrConfig) {
    const coords = [];
    const colors = [];
    const sizes = [];
    const opacities = [];
    const randoms = [];

    for (let i = 0; i < count; i++) {
      const r = 1.57e-2 * Math.sqrt(Math.random());
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const pos = new Vector3(
        r * Math.sin(phi) * Math.cos(theta),
        r * Math.sin(phi) * Math.sin(theta),
        r * Math.cos(phi)
      );

      const { color, size } = colorSizeAleat(attrConfig);
      coords.push(pos);
      colors.push(color);
      sizes.push(size);
      opacities.push(1.0);
      randoms.push(Math.random() * 100);
    }

    return { coords, colors, sizes, opacities, randoms };
  }

  _createPoints({ coords, colors, sizes, opacities, randoms }) {
    const geo = new BufferGeometry();
    geo.setAttribute(
      'position',
      new Float32BufferAttribute(coords.flatMap(v => [v.x, v.y, v.z]), 3)
    );
    geo.setAttribute(
      'color',
      new Float32BufferAttribute(colors.flatMap(c => [c.r, c.g, c.b]), 3)
    );
    geo.setAttribute('sizes', new Float32BufferAttribute(sizes, 1));
    geo.setAttribute('opacity', new Float32BufferAttribute(randoms, 1));
    geo.setAttribute('randomValue', new Float32BufferAttribute(opacities, 1));

    const material = new ShaderMaterial({
      uniforms: {
        globalOpacity: { value: this.CONFIG.globalOpacity },
        cameraPos: { value: sceneManager.camera.position },
        center: { value: this.mesh.position },
        maxRadius: { value: this.CONFIG.maxRadius },
        maxCamDist: { value: this.CONFIG.maxCameraDist },
        sunPos: { value: this.center.mesh.position },
        minFalloffExp: { value: this.CONFIG.minFalloffExp },
        maxFalloffExp: { value: this.CONFIG.maxFalloffExp }
      },
      vertexShader: vertexComet,
      fragmentShader: fragmentComet,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false
    });

    const points = new Points(geo, material);
    points.position.copy(this.mesh.position);
    this.scene.add(points);

    return points;
  }

  _updateTail(points, attrs, type, opacityFactor) {
    const {
      gasSphereRadius,
      dustSphereRadius,
      gasSpeedFactor,
      dustSpeedFactor,
      resetProbability,
    } = this.CONFIG;

    const count = attrs.coords.length;
    const positions = points.geometry.attributes.position.array;
    const sizes = points.geometry.attributes.sizes.array;
    const opacityArr = points.geometry.attributes.opacity.array;

    const sphereRadius = type === 'gas' ? gasSphereRadius : dustSphereRadius;
    const speed = type === 'gas' ? gasSpeedFactor : dustSpeedFactor;
    const randomReset = resetProbability.default;
    const randomMoveProb = resetProbability.randomMove[type];

    const toSun = new Vector3().subVectors(this.mesh.position, this.center.mesh.position).normalize();

    for (let i = 0; i < count; i++) {
      const idx3 = i * 3;
      positions[idx3 + 2] -= speed;

      if (Math.random() < randomMoveProb) {
        positions[idx3] += (Math.random() * 2 - 1) * sphereRadius * (type === 'gas' ? 4 : 3);
        positions[idx3 + 1] += (Math.random() * 2 - 1) * sphereRadius * (type === 'gas' ? 4 : 3);
      }
      if (positions[idx3 + 2] < sphereRadius * 15 && Math.random() < randomReset) {
        positions[idx3] = (Math.random() * 1.5 - 0.75) * sphereRadius;
        positions[idx3 + 1] = (Math.random() * 1.5 - 0.75) * sphereRadius;
        positions[idx3 + 2] = (Math.random() * 1.5 - 0.75) * sphereRadius;
      }

      if (Math.random() < 0.05) {
        positions[idx3] += (Math.random() * 2 - 1) * sphereRadius * 2;
        positions[idx3 + 1] += (Math.random() * 2 - 1) * sphereRadius * 2;
        positions[idx3 + 2] += (Math.random() * 2 - 1) * sphereRadius * 2;
      }

      if (type === 'dust') {
        const offset = toSun.clone().multiplyScalar(0.003 * positions[idx3 + 2]);
        positions[idx3] += offset.x;
        positions[idx3 + 1] += offset.y;
        positions[idx3 + 2] += offset.z;
      }

      const dist = MathUtils.clamp(
        Math.sqrt(
          positions[idx3] ** 2 + positions[idx3 + 1] ** 2 + positions[idx3 + 2] ** 2
        ),
        1,
        type === 'gas' ? 6 : 7
      );
      sizes[i] = attrs.sizes[i] * dist;
      opacityArr[i] = MathUtils.clamp(
        MathUtils.mapLinear(dist, 0, type === 'gas' ? 6 : 7, 1, 0),
        0,
        1
      );
    }

    ['position', 'sizes', 'opacity'].forEach(attr => {
      points.geometry.attributes[attr].needsUpdate = true;
    });

    points.material.uniforms.globalOpacity.value = Math.min(0.1, opacityFactor);
  }
  animate() {
    const distToSun = this.mesh.position.distanceTo(this.center.mesh.position);
    const opacityFactor = MathUtils.mapLinear(distToSun, 0, 176, 0.1, 0);

    this._updateTail(this.gas, this.attrsGas, 'gas', opacityFactor * 0.9);
    this._updateTail(this.dust, this.attrsDust, 'dust', opacityFactor);
    [this.gas, this.dust].forEach(points => {
      points.lookAt(this.center.mesh.position);
      points.position.copy(this.mesh.position);
      const { camera } = sceneManager;
      points.material.uniforms.cameraPos.value.copy(camera.position);
      points.material.uniforms.center.value.copy(this.mesh.position);
      points.material.uniforms.sunPos.value.copy(this.center.mesh.position);
    });

    this._toggleVisibility(distToSun);
  }

  _toggleVisibility(distToSun) {
    if (distToSun >= 200 && !this._removed) {
      this._disposePoints(this.gas);
      this._disposePoints(this.dust);
      this._removed = true;
    } else if (distToSun < 200 && this._removed) {
      this.dust = this._createPoints(this.attrsDust);
      this.gas = this._createPoints(this.attrsGas);
      this._removed = false;
    }
  }
  _disposePoints(points) {
    this.scene.remove(points);
    points.geometry.dispose();
    points.material.dispose();
  }
}
